import createPersistedReducer from '@/redux/lib/createPersistedReducer.js';
import getPieveSessionCompanyId from '@/lib/auth/getPieveSessionCompanyId.js';
import resolveSessionCompanyId from '@/redux/user/resolveSessionCompanyId.js';
import toSafeUserPermissions from '@/redux/user/toSafeUserPermissions.js';
import toSafeUserStateString from '@/redux/user/toSafeUserStateString.js';
import { hasOwn } from '@shared/utils/hasOwn.js';

const initialAuthState = {
  userId: null,
  companyId: null,
  company: null,
  admin: null,
  impersonatorUserId: null,
  role: null,
  roleId: null,
  permissions: null
};

const mergeAuthPatch = (currentAuth, payload) => ({
  userId: hasOwn(payload, 'userId') ? toSafeUserStateString(payload?.userId) : currentAuth?.userId || null,
  companyId: hasOwn(payload, 'companyId') ? toSafeUserStateString(payload?.companyId) : currentAuth?.companyId || null,
  company: hasOwn(payload, 'company') && payload?.company && typeof payload.company === 'object' ? payload.company : (currentAuth?.company ?? null),
  admin: hasOwn(payload, 'admin') ? (typeof payload?.admin === 'boolean' ? payload.admin : null) : (currentAuth?.admin ?? null),
  impersonatorUserId: hasOwn(payload, 'impersonatorUserId') ? toSafeUserStateString(payload?.impersonatorUserId) : currentAuth?.impersonatorUserId || null,
  role: hasOwn(payload, 'role') ? toSafeUserStateString(payload?.role) : currentAuth?.role || null,
  roleId: hasOwn(payload, 'roleId') ? toSafeUserStateString(payload?.roleId) : currentAuth?.roleId || null,
  permissions: hasOwn(payload, 'permissions') ? toSafeUserPermissions(payload?.permissions, { allowNull: true }) : (currentAuth?.permissions ?? null)
});

/* The browser session (email + password login, cookie held by the browser). `unknown` until the first
   GET /v1/auth/session answers; never persisted, so every load asks the server again. */
const initialSessionState = { status: 'unknown', user: null };

const applySignedInSession = (state, payload) => ({
  ...state,
  session: { status: 'signedIn', user: payload?.data?.user || null }
});

const initialState = {
  session: initialSessionState,
  // Whether the signed-in context (organizations, role, preferences) is loaded: idle, loading, ready or error.
  sessionContext: { status: 'idle', message: null },
  auth: initialAuthState,
  activations: [],
  feedbackNudgesByCompanyId: {},
  companies: null,
  sessionCompanyId: getPieveSessionCompanyId()
};

const applyUserFeedbackNudgeResult = (state, payload) => {
  const companyId = toSafeUserStateString(payload?.data?.companyId);

  if (!companyId || !payload?.data?.nudge) {
    return state;
  }

  return {
    ...state,
    feedbackNudgesByCompanyId: {
      ...state.feedbackNudgesByCompanyId,
      [companyId]: payload.data.nudge
    }
  };
};

const applyOrganizationSetupResult = (state, payload) => {
  const companies = (Array.isArray(payload?.data?.companies) ? payload.data.companies : []).filter((company) => {
    return Boolean(company && typeof company === 'object');
  });
  const activeCompanyId = toSafeUserStateString(payload?.data?.activeCompanyId);

  return {
    ...state,
    companies,
    sessionCompanyId: activeCompanyId || state?.sessionCompanyId || null
  };
};

/* `auth` is persisted so a reload can draw the app before the session check answers; App.jsx discards it at boot unless
   canRenderFromAuthSnapshot allows it (docs/plans/active/pieve/2026-11-03-pieve-fast-boot). */
export default createPersistedReducer(
  initialState,
  {
    SESSION_CONTEXT_STATUS: (state, payload) => ({
      ...state,
      sessionContext: { status: payload?.status || 'idle', message: payload?.message || null }
    }),

    GET_AUTH_SESSION_SUCCESS: applySignedInSession,

    GET_AUTH_SESSION_FAIL: (state) => ({
      ...state,
      session: { status: 'signedOut', user: null }
    }),

    SIGN_IN_WITH_PASSWORD_SUCCESS: applySignedInSession,

    SIGN_UP_WITH_PASSWORD_SUCCESS: applySignedInSession,

    RESET_PASSWORD_SUCCESS: applySignedInSession,

    ACCEPT_MEMBER_INVITE_SUCCESS: applySignedInSession,

    SIGN_OUT_SUCCESS: () => ({
      ...initialState,
      session: { status: 'signedOut', user: null },
      sessionCompanyId: null
    }),

    DISCARD_PIEVE_AUTH_SNAPSHOT: (state) => ({
      ...state,
      auth: initialAuthState
    }),

    GET_PIEVE_AUTH_ME_SUCCESS: (state, payload) => {
      const responseUserId = toSafeUserStateString(payload?.data?.userId);
      const expectedUserId = toSafeUserStateString(payload?.meta?.userId);

      if (expectedUserId && responseUserId !== expectedUserId) {
        return state;
      }

      return {
        ...state,
        auth: mergeAuthPatch(initialAuthState, payload?.data)
      };
    },

    GET_PIEVE_USER_ACTIVATION_SUCCESS: (state, payload) => ({
      ...state,
      activations: Array.isArray(payload?.data) ? payload.data : []
    }),

    GENERATE_PIEVE_USER_ACTIVATION_SUCCESS: (state, payload) => ({
      ...state,
      activations: Array.isArray(payload?.data) ? payload.data : state.activations
    }),

    GENERATE_PIEVE_USER_ACTIVATION_FAIL: (state, payload) => ({
      ...state,
      activations: Array.isArray(payload?.data) ? payload.data : state.activations
    }),

    DISMISS_PIEVE_USER_ACTIVATION_SUCCESS: (state, payload) => ({
      ...state,
      activations: Array.isArray(payload?.data) ? payload.data : state.activations
    }),

    ACCEPT_PIEVE_USER_ACTIVATION_SUCCESS: (state, payload) => ({
      ...state,
      activations: Array.isArray(payload?.data) ? payload.data : state.activations
    }),

    GET_PIEVE_USER_FEEDBACK_NUDGE_SUCCESS: applyUserFeedbackNudgeResult,

    DISMISS_PIEVE_USER_FEEDBACK_NUDGE_SUCCESS: applyUserFeedbackNudgeResult,

    SUBMIT_PIEVE_USER_FEEDBACK_SUCCESS: applyUserFeedbackNudgeResult,

    GET_PIEVE_USER_COMPANIES_SUCCESS: (state, payload) => {
      const expectedUserId = toSafeUserStateString(payload?.meta?.userId);
      const currentUserId = toSafeUserStateString(state?.auth?.userId);

      if (expectedUserId && currentUserId && expectedUserId !== currentUserId) {
        return state;
      }

      const companies = (Array.isArray(payload?.data?.companies) ? payload.data.companies : []).filter((company) => {
        return Boolean(company && typeof company === 'object');
      });
      const nextSessionCompanyId = resolveSessionCompanyId({
        companies,
        preferredCompanyId: payload?.meta?.preferredCompanyId || state?.sessionCompanyId || state?.auth?.companyId
      });

      return {
        ...state,
        companies,
        sessionCompanyId: nextSessionCompanyId
      };
    },

    SWITCH_PIEVE_USER_COMPANY_SUCCESS: (state, payload) => ({
      ...state,
      sessionCompanyId: toSafeUserStateString(payload?.meta?.companyId)
    }),

    CONVERT_PIEVE_COMPANY_TO_ORGANIZATION_SUCCESS: applyOrganizationSetupResult,

    CREATE_PIEVE_ORGANIZATION_FROM_PERSONAL_ACCOUNT_SUCCESS: applyOrganizationSetupResult,

    SET_PIEVE_SESSION_COMPANY_ID: (state, payload) => ({
      ...state,
      sessionCompanyId: toSafeUserStateString(payload)
    }),

    RESET_PIEVE_USER_STATE: (state) => ({
      ...initialState,
      session: state.session,
      auth: {
        ...initialAuthState,
        userId: null,
        companyId: null,
        company: null,
        admin: null,
        impersonatorUserId: null,
        role: null,
        roleId: null,
        permissions: null
      },
      activations: [],
      companies: null,
      sessionCompanyId: null
    })
  },
  {
    key: 'pieve-user',
    whitelist: ['auth']
  }
);
